/**
 * Pure-JS XMLTV EPG parser + helpers.
 *
 * Browser DOMParser handles XMLTV fine, but a 50 MB EPG can be heavy.
 * For now we parse synchronously; if perf becomes an issue we can move to a
 * Web Worker (the cache layer already keys by URL so a worker would slot in
 * without touching consumers).
 */

function parseXmlTvTime(value) {
  if (!value) return null;
  const m = value.trim().match(/^(\d{14})(?:\s*([+-]\d{4}))?$/);
  if (!m) return null;
  const [, dt, tz] = m;
  // dt = YYYYMMDDHHMMSS
  const iso = `${dt.slice(0, 4)}-${dt.slice(4, 6)}-${dt.slice(6, 8)}T${dt.slice(
    8,
    10
  )}:${dt.slice(10, 12)}:${dt.slice(12, 14)}${
    tz ? `${tz.slice(0, 3)}:${tz.slice(3)}` : "Z"
  }`;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Normalise a channel display name so we can match across slight
 * formatting differences between M3U `tvg-name` / channel name and
 * XMLTV `<display-name>` (case, whitespace, punctuation, common
 * "[group]" / "HD" / "(S)" suffixes).
 */
function normaliseName(s) {
  return (s || "")
    .toLowerCase()
    .replace(/\[[^\]]*\]|\([^)]*\)/g, " ") // strip [GROUP] and (S) tags
    .replace(/\b(hd|sd|fhd|uhd|4k|raw|backup|vip)\b/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

/**
 * Parse XMLTV text into a map: { tvgId: [{title, description, start, stop, start_ts, stop_ts}] }.
 * Sorted ascending by start.
 *
 * We also index each programme list under a normalised `<display-name>`
 * key so the LiveTV page can fall back to channel-name matching when
 * the M3U `tvg-id` doesn't line up with the XMLTV `channel id` (which
 * is the #1 reason "EPG isn't showing" for a given playlist).
 */
export async function parseXmlTv(text) {
  if (!text || typeof text !== "string") return {};
  // v1.4.20 — Hard XMLTV size cap.
  //
  // `DOMParser().parseFromString(text, "text/xml")` allocates a full
  // DOM tree (typically 3-5x the source text size) on top of the
  // text buffer itself. On Smart TV WebViews with a ~256 MB heap
  // budget, an XMLTV file north of ~30 MB is enough to OOM the tab
  // in the middle of a cold-start playlist load — matching the
  // "AAB crashes on Smart TV during playlist load" report.
  //
  // We fail-safe rather than fail-hard: if the EPG is too big to
  // parse in-place, return an empty map so the app boots cleanly.
  // The Settings → EPG status card surfaces the skip so users know
  // WHY they don't see programme names on the live grid. Manual
  // "Reload Playlist" is unaffected — it goes through the same
  // guard but on that path the user has explicitly opted-in.
  const MAX_XMLTV_BYTES = 30 * 1024 * 1024; // 30 MB
  if (text.length > MAX_XMLTV_BYTES) {
    try {
      // eslint-disable-next-line no-console
      console.warn(
        `[epgParser] XMLTV size ${text.length} exceeds ${MAX_XMLTV_BYTES}-byte cap — skipping DOMParser to avoid OOM on Smart TVs.`,
      );
    } catch {/* noop */}
    return {};
  }
  let doc;
  try {
    doc = new DOMParser().parseFromString(text, "text/xml");
  } catch (e) {
    // v1.4.20 — DOMParser itself can throw a low-level allocation
    // failure on constrained devices even before a `parsererror`
    // node is emitted. Catch + no-op keeps the app booting.
    try {
      // eslint-disable-next-line no-console
      console.warn("[epgParser] DOMParser threw:", e?.message || e);
    } catch {/* noop */}
    return {};
  }
  // Check parse error
  if (doc.getElementsByTagName("parsererror").length) return {};

  // 1. Build the canonical id → programmes map. v1.3.40 — yield to the
  // event loop every 5k programmes so giant XMLTV feeds don't freeze
  // the Smart TV renderer (590k-channel providers can ship 1M+
  // programmes).
  const YIELD_EVERY = 5000;
  const byChannel = {};
  const programmes = doc.getElementsByTagName("programme");
  for (let i = 0; i < programmes.length; i++) {
    const el = programmes[i];
    const channel = el.getAttribute("channel") || "";
    const start = parseXmlTvTime(el.getAttribute("start") || "");
    const stop = parseXmlTvTime(el.getAttribute("stop") || "");
    if (!channel || !start || !stop) {
      if ((i + 1) % YIELD_EVERY === 0) await new Promise((r) => setTimeout(r, 0));
      continue;
    }
    const titleEl = el.getElementsByTagName("title")[0];
    const descEl = el.getElementsByTagName("desc")[0];
    const prog = {
      title: titleEl ? (titleEl.textContent || "").trim() : "",
      description: descEl ? (descEl.textContent || "").trim() : null,
      start: start.toISOString(),
      stop: stop.toISOString(),
      start_ts: Math.floor(start.getTime() / 1000),
      stop_ts: Math.floor(stop.getTime() / 1000),
    };
    (byChannel[channel] ||= []).push(prog);
    if ((i + 1) % YIELD_EVERY === 0) await new Promise((r) => setTimeout(r, 0));
  }
  for (const list of Object.values(byChannel)) {
    list.sort((a, b) => a.start_ts - b.start_ts);
  }

  // 2. Add fallback keys for every channel's <display-name> so
  //    `getChannelProgrammes()` below can match against the M3U
  //    channel name when tvg-id doesn't agree with the XMLTV id.
  const channelEls = doc.getElementsByTagName("channel");
  for (let i = 0; i < channelEls.length; i++) {
    const ch = channelEls[i];
    const id = ch.getAttribute("id") || "";
    if (!id) continue;
    const list = byChannel[id];
    if (!list) continue;
    const nameEls = ch.getElementsByTagName("display-name");
    for (let j = 0; j < nameEls.length; j++) {
      const raw = (nameEls[j].textContent || "").trim();
      const norm = normaliseName(raw);
      if (norm && !byChannel[norm]) byChannel[norm] = list;
    }
  }

  return byChannel;
}

/**
 * Look up the programme list for a channel using the strongest match
 * we can find. The M3U `tvg-id` is the canonical key but providers are
 * sloppy — fall through to `tvg-name`, then a normalised version of
 * the channel display name. Returns `[]` if nothing matches.
 */
export function getChannelProgrammes(byChannel, channel) {
  if (!byChannel || !channel) return [];
  const keys = [
    channel.tvg_id,
    channel.tvg_name && normaliseName(channel.tvg_name),
    channel.name && normaliseName(channel.name),
  ].filter(Boolean);
  for (const k of keys) {
    const list = byChannel[k];
    if (list && list.length) return list;
  }
  return [];
}

/** Past programmes for a given tvg-id, newest first. */
export function pastProgrammes(byChannel, channelOrId, { days = 7, limit = 80 } = {}) {
  const list =
    typeof channelOrId === "string"
      ? byChannel[channelOrId] || []
      : getChannelProgrammes(byChannel, channelOrId);
  const nowTs = Math.floor(Date.now() / 1000);
  const cutoff = nowTs - days * 86400;
  return list
    .filter((p) => p.stop_ts <= nowTs && p.start_ts >= cutoff)
    .sort((a, b) => b.start_ts - a.start_ts)
    .slice(0, limit);
}

/** Currently-airing programme for a channel — accepts either a tvg-id
 *  string OR the full channel object (so we can fall through to name
 *  matching when the tvg-id is missing or mismatched). */
export function currentProgramme(byChannel, channelOrId) {
  const list =
    typeof channelOrId === "string"
      ? byChannel[channelOrId] || []
      : getChannelProgrammes(byChannel, channelOrId);
  const nowTs = Math.floor(Date.now() / 1000);
  return list.find((p) => p.start_ts <= nowTs && nowTs <= p.stop_ts) || null;
}

/**
 * Build the actual catch-up stream URL for a past programme.
 *
 * Mirrors backend/epg_parser.py build_catchup_url so the player no longer
 * depends on the backend for this.
 */
export function buildCatchupUrl({
  baseStreamUrl,
  catchupSource,
  catchupType,
  startTs,
  durationSeconds,
}) {
  const endTs = startTs + durationSeconds;
  const nowTs = Math.floor(Date.now() / 1000);

  if (catchupSource) {
    let url = catchupSource;
    const repl = {
      "${start}": String(startTs),
      "${end}": String(endTs),
      "${timestamp}": String(startTs),
      "${offset}": String(nowTs - startTs),
      "${duration}": String(durationSeconds),
      "{utc}": String(startTs),
      "{utcend}": String(endTs),
      "{lutc}": String(nowTs),
      "{duration}": String(durationSeconds),
    };
    for (const [k, v] of Object.entries(repl)) {
      url = url.split(k).join(v);
    }
    const d = new Date(startTs * 1000);
    const pad = (n) => String(n).padStart(2, "0");
    url = url
      .replace("{Y}", String(d.getUTCFullYear()))
      .replace("{m}", pad(d.getUTCMonth() + 1))
      .replace("{d}", pad(d.getUTCDate()))
      .replace("{H}", pad(d.getUTCHours()))
      .replace("{M}", pad(d.getUTCMinutes()))
      .replace("{S}", pad(d.getUTCSeconds()));
    return url;
  }

  const ctype = (catchupType || "default").toLowerCase();
  if (ctype === "append" || ctype === "shift") {
    const sep = baseStreamUrl.includes("?") ? "&" : "?";
    return `${baseStreamUrl}${sep}utc=${startTs}&lutc=${nowTs}`;
  }

  // Flussonic / default: /<stream>/index.m3u8 → /<stream>/timeshift_abs-<ts>.m3u8
  if (baseStreamUrl.endsWith(".m3u8")) {
    return baseStreamUrl.replace(/\/[^/]*\.m3u8$/, `/timeshift_abs-${startTs}.m3u8`);
  }
  const sep = baseStreamUrl.includes("?") ? "&" : "?";
  return `${baseStreamUrl}${sep}utc=${startTs}`;
}